import { cn } from '@neo/lib/utils'
import { Popover, PopoverContent, PopoverTrigger } from '@neo/primitives/popover'
import { Tooltip } from '@neo/primitives/tooltip'
import { useUpdateNodeInternals } from '@xyflow/react'
import { type ReactNode, useEffect, useState } from 'react'
import { MenuNodeID } from '../../config/nodes.config'
import { useFlowStore } from '../../store'
import type { GenericNode, OutputState } from '../../types'
import { isAiAgentOrActionNode } from '../../utils/ai-agent-utils'
import { PATH_PICKER_SURFACE, PathPicker } from '../path-picker'
import { ZoomScaled } from '../zoom-scaled'
import { EdgeForm } from './edge-form'

interface EdgePopoverProps {
  id: string
  sourceNode?: GenericNode
  opStates: OutputState[]
  // Picker heading override — set for section-scoped pending edges.
  pathsTitle?: string
  opStateName: string
  target: string
  trigger: ReactNode
}

export const EdgePopover = ({
  id,
  sourceNode,
  opStates,
  pathsTitle,
  opStateName,
  target,
  trigger,
}: EdgePopoverProps) => {
  const [popoverOpen, setPopoverOpen] = useState(false)
  const updateNodeInternals = useUpdateNodeInternals()
  const isEditing = useFlowStore((state) => state.isEditing)

  const handleOpStateClick = (opState: OutputState) => {
    setPopoverOpen(false)
    const { flowInstance } = useFlowStore.getState()
    if (!flowInstance) return

    flowInstance.updateEdge(id, {
      sourceHandle: opState.id,
    })
    if (sourceNode?.id) updateNodeInternals(sourceNode?.id)
  }

  const isAiNode = isAiAgentOrActionNode(sourceNode?.type)

  // Check if the trigger should be enabled
  const isTriggerEnabled = isAiNode || (!isAiNode && isEditing)

  useEffect(() => {
    if (isAiNode) return
    setPopoverOpen(opStateName === 'default-edge' && target !== MenuNodeID)
  }, [opStateName, target, isAiNode])

  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger asChild disabled={!isTriggerEnabled}>
        {!isTriggerEnabled ? (
          <Tooltip content="Enable edit mode to modify connections">
            <div
              className={cn(
                isTriggerEnabled && 'cursor-pointer',
                !isTriggerEnabled && 'cursor-not-allowed pointer-events-none',
              )}
              data-testid="edge-edit-trigger"
              aria-label="Edit connection"
            >
              {trigger}
            </div>
          </Tooltip>
        ) : (
          <div
            className={cn(
              isTriggerEnabled && 'cursor-pointer',
              !isTriggerEnabled && 'cursor-not-allowed pointer-events-none',
            )}
            data-testid="edge-edit-trigger"
            aria-label="Edit connection"
          >
            {trigger}
          </div>
        )}
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          'p-0 w-auto edge-popover',
          // The path picker paints its own surface INSIDE the zoom wrapper —
          // chrome on this outer shell would keep the unscaled layout size and
          // read as dead padding at zoom < 1. The AI intent form is unscaled
          // and keeps the stock popover chrome.
          isAiNode ? 'min-w-[150px]' : 'border-0 bg-transparent shadow-none',
        )}
        align="start"
      >
        {isAiNode ? (
          <EdgeForm id={id} sourceNode={sourceNode} closePopover={() => setPopoverOpen(false)} />
        ) : (
          <ZoomScaled>
            <div className={PATH_PICKER_SURFACE}>
              <PathPicker opStates={opStates} title={pathsTitle} onSelect={handleOpStateClick} />
            </div>
          </ZoomScaled>
        )}
      </PopoverContent>
    </Popover>
  )
}
